/*
 * SS Card Carousel — infinite (seamless, clone-based) product carousels.
 * 2026-09-24 (client: Olivia). Loaded by snippets/ss-card-carousel.liquid.
 *
 * Contract:  <div data-ssc-loop>                 viewport (position: relative)
 *              <button data-ssc-prev>             optional
 *              <ul data-ssc-track> <li>…</li> </ul>
 *              <button data-ssc-next>             optional
 *
 * How the loop works: when the track actually overflows, one full copy of
 * the slides is cloned before and one after the originals (clones are
 * inert + aria-hidden so keyboard/AT only ever see the real cards). The
 * track starts on the middle (original) set; whenever scrolling settles in
 * a clone set it jumps by exactly one set width — an invisible jump, since
 * the content at both positions is identical — so Next past the last card
 * shows the first again (and Prev from the first shows the last), for
 * arrows and native swipe alike. If the row fits without scrolling the
 * track is left untouched and the arrows are hidden (.ssc-static).
 *
 * Works for server-rendered sections and for content injected later
 * (product-recommendations) via a MutationObserver. Idempotent.
 *
 * SS 2026-10-08 (client: Olivia): "carousels get stuck / last card cut off".
 * Every carousel except Dawn's <slider-component> (featured-collection, which
 * keeps the scroll-based Loop above because Dawn's own slider JS drives it)
 * now uses the TRANSFORM engine (Tx below):
 *   - track is wrapped in .ssc-clip (overflow clip) and moved with
 *     translate3d + a CSS transition; clones before/after the originals make
 *     it endlessly revolving in both directions (index is modular).
 *   - whole cards per view: slide width = (available - gaps) / per-view.
 *     per-view comes from the CSS custom property --ssc-per on the
 *     [data-ssc-loop] element (sections set 4 / 2 / 1.5 via settings); if
 *     unset, as many whole cards as fit at their natural width.
 *   - if every card already fits, nothing moves and the arrows hide.
 *   - swipe / mouse-drag, arrow keys, optional autoplay via
 *     data-ssc-autoplay="<seconds>" (pauses on hover, focus, drag, hidden tab
 *     and while off-screen).
 */
(function () {
  if (window.__sscCarouselInit) return;
  window.__sscCarouselInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STRIP_CLASSES = ['scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen', 'scroll-trigger--cancel'];

  function rectLeft(el) { return el.getBoundingClientRect().left; }
  function originalsOf(track) {
    return Array.prototype.filter.call(track.children, function (c) { return !c.hasAttribute('data-ssc-clone'); });
  }
  function placeArrows(root, track) {
    var media = null;
    var o = originalsOf(track);
    for (var i = 0; i < o.length && !media; i++) {
      media = o[i].querySelector('.ssc-media, .card__inner') || null;
    }
    if (!media) return;
    var r = media.getBoundingClientRect();
    if (!r.height) return;
    var top = r.top - root.getBoundingClientRect().top + r.height / 2;
    root.style.setProperty('--ssc-arrow-top', Math.round(top) + 'px');
  }

  function Loop(root) {
    this.root = root;
    this.track = root.querySelector('[data-ssc-track]');
    this.prev = root.querySelector('[data-ssc-prev]');
    this.next = root.querySelector('[data-ssc-next]');
    this.built = false;
    this.setW = 0;
    this.step = 0;
    this.target = null;
    this.dragging = false;
    this.settleTimer = null;
    this.lastWidth = 0;
    this.bind();
    this.refresh();
  }

  Loop.prototype.originals = function () { return originalsOf(this.track); };

  Loop.prototype.jump = function (x) {
    var t = this.track;
    var snap = t.style.scrollSnapType;
    t.style.scrollSnapType = 'none';
    t.scrollLeft = x;
    void t.offsetWidth; // flush before snapping is restored
    t.style.scrollSnapType = snap;
  };

  function makeClone(node) {
    var c = node.cloneNode(true);
    c.setAttribute('data-ssc-clone', '');
    c.setAttribute('aria-hidden', 'true');
    c.inert = true;
    c.removeAttribute('id');
    c.removeAttribute('data-shopify-editor-block');
    var all = c.querySelectorAll('[id], a, button, input, select, textarea, [tabindex]');
    for (var i = 0; i < all.length; i++) {
      all[i].removeAttribute('id');
      all[i].setAttribute('tabindex', '-1');
    }
    var anim = [c].concat(Array.prototype.slice.call(c.querySelectorAll('.' + STRIP_CLASSES.join(',.'))));
    anim.forEach(function (el) { STRIP_CLASSES.forEach(function (k) { el.classList.remove(k); }); });
    return c;
  }

  Loop.prototype.teardown = function () {
    if (!this.built) return;
    var t = this.track;
    var idx = this.currentIndex();
    Array.prototype.slice.call(t.querySelectorAll(':scope > [data-ssc-clone]')).forEach(function (c) { c.remove(); });
    this.built = false;
    this.jump(Math.max(0, idx) * this.step);
  };

  Loop.prototype.currentIndex = function () {
    var o = this.originals();
    if (!this.built || !this.step || !o.length) return 0;
    var n = o.length;
    var i = Math.round((this.track.scrollLeft - this.setW) / this.step);
    return ((i % n) + n) % n;
  };

  Loop.prototype.build = function () {
    var t = this.track;
    var o = this.originals();
    this.root.classList.remove('ssc-static', 'ssc-looping');
    if (o.length < 2 || !t.clientWidth) { this.root.classList.add('ssc-static'); return; }
    if (t.scrollWidth <= t.clientWidth + 4) { this.root.classList.add('ssc-static'); return; }

    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();
    var self = this;
    o.forEach(function (n) { before.appendChild(makeClone(n)); after.appendChild(makeClone(n)); });
    var firstClone = before.firstChild;
    t.insertBefore(before, t.firstChild);
    t.appendChild(after);

    this.setW = rectLeft(o[0]) - rectLeft(firstClone);
    this.step = rectLeft(o[1]) - rectLeft(o[0]);
    if (!(this.setW > 0) || !(this.step > 0)) { // measurement failed: undo
      Array.prototype.slice.call(t.querySelectorAll(':scope > [data-ssc-clone]')).forEach(function (c) { c.remove(); });
      this.root.classList.add('ssc-static');
      return;
    }
    this.built = true;
    this.root.classList.add('ssc-looping');
    this.jump(this.setW + (this.pendingIndex || 0) * this.step);
    this.pendingIndex = 0;
  };

  Loop.prototype.normalize = function () {
    if (!this.built || this.dragging) return;
    var x = this.track.scrollLeft, w = this.setW;
    if (x < w * 0.5) this.jump(x + w);
    else if (x >= w * 1.5) this.jump(x - w);
  };

  Loop.prototype.go = function (dir) {
    var t = this.track;
    if (!this.built) {
      // Not overflowing / not built: simple wraparound fallback.
      var max = t.scrollWidth - t.clientWidth;
      if (max <= 2) return;
      var stepF = (t.firstElementChild && t.firstElementChild.getBoundingClientRect().width) || t.clientWidth * 0.8;
      var dest = t.scrollLeft + dir * stepF;
      if (dir > 0 && t.scrollLeft >= max - 2) dest = 0;
      if (dir < 0 && t.scrollLeft <= 2) dest = max;
      t.scrollTo({ left: dest, behavior: reduceMotion ? 'auto' : 'smooth' });
      return;
    }
    var from = this.target != null ? this.target : t.scrollLeft;
    var to = from + dir * this.step;
    var w = this.setW;
    if (to < w * 0.5 || to > w * 1.5) {
      var shift = to < w * 0.5 ? w : -w;
      this.jump(t.scrollLeft + shift);
      to += shift;
    }
    this.target = to;
    t.scrollTo({ left: to, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.scheduleSettle(900);
  };

  Loop.prototype.scheduleSettle = function (ms) {
    var self = this;
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(function () { self.target = null; self.normalize(); }, ms);
  };

  Loop.prototype.placeArrows = function () { placeArrows(this.root, this.track); };

  Loop.prototype.refresh = function () {
    var w = this.track.clientWidth;
    var t = this.track;
    // same width and still a horizontal scroller: nothing to rebuild
    if (this.built && w === this.lastWidth && t.scrollWidth > t.clientWidth + 4) { this.placeArrows(); return; }
    this.lastWidth = w;
    if (this.built) { this.pendingIndex = this.currentIndex(); this.teardown(); }
    this.build();
    this.placeArrows();
  };

  Loop.prototype.bind = function () {
    var self = this, t = this.track;
    function onArrow(dir) {
      return function (e) {
        e.preventDefault();
        e.stopPropagation(); // keep legacy document-level arrow handlers out
        self.go(dir);
      };
    }
    if (this.prev) this.prev.addEventListener('click', onArrow(-1));
    if (this.next) this.next.addEventListener('click', onArrow(1));

    t.addEventListener('scroll', function () {
      if (self.target != null) { self.scheduleSettle(160); return; }
      self.scheduleSettle(140);
    }, { passive: true });
    if ('onscrollend' in window) {
      t.addEventListener('scrollend', function () {
        clearTimeout(self.settleTimer);
        self.target = null;
        self.normalize();
      });
    }
    t.addEventListener('pointerdown', function () { self.dragging = true; }, { passive: true });
    t.addEventListener('touchstart', function () { self.dragging = true; }, { passive: true });
    function up() { self.dragging = false; self.scheduleSettle(180); }
    window.addEventListener('pointerup', up, { passive: true });
    t.addEventListener('touchend', up, { passive: true });
    t.addEventListener('touchcancel', up, { passive: true });

    // keyboard: arrow keys while focus is inside the track
    t.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.go(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(-1); }
    });

    if ('ResizeObserver' in window) {
      var rt;
      new ResizeObserver(function () {
        clearTimeout(rt);
        rt = setTimeout(function () { self.refresh(); }, 60);
      }).observe(t);
    } else {
      window.addEventListener('resize', function () { self.refresh(); });
    }
    // images decoding can shift the media box on first paint
    window.addEventListener('load', function () { self.placeArrows(); });
  };


  /* ---------------- Tx: transform-based infinite carousel ---------------- */
  function Tx(root) {
    this.root = root;
    this.track = root.querySelector('[data-ssc-track]');
    this.prev = root.querySelector('[data-ssc-prev]');
    this.next = root.querySelector('[data-ssc-next]');
    this.on = false;
    this.i = 0;
    this.n = 0;
    this.step = 0;
    this.per = 1;
    this.hover = false;
    this.focus = false;
    this.visible = true;
    this.wrap();
    this.bind();
    this.refresh();
  }

  Tx.prototype.wrap = function () {
    var t = this.track, p = t.parentNode;
    if (p.classList && p.classList.contains('ssc-clip')) { this.clip = p; return; }
    var c = document.createElement('div');
    c.className = 'ssc-clip';
    p.insertBefore(c, t);
    c.appendChild(t);
    this.clip = c;
  };

  Tx.prototype.teardown = function () {
    var t = this.track;
    Array.prototype.slice.call(t.querySelectorAll(':scope > [data-ssc-clone]')).forEach(function (c) { c.remove(); });
    this.on = false;
    this.root.classList.remove('ssc-tx', 'ssc-looping');
    this.root.style.removeProperty('--ssc-w');
    t.style.transform = '';
    t.style.transition = '';
  };

  Tx.prototype.build = function () {
    var t = this.track, o = originalsOf(t);
    var before = document.createDocumentFragment(), after = document.createDocumentFragment();
    o.forEach(function (n) { before.appendChild(makeClone(n)); after.appendChild(makeClone(n)); });
    t.insertBefore(before, t.firstChild);
    t.appendChild(after);
    // off-screen originals/clones sit behind an overflow clip: load them now
    Array.prototype.forEach.call(t.querySelectorAll('img[loading="lazy"]'), function (im) { im.loading = 'eager'; });
    this.on = true;
  };

  Tx.prototype.refresh = function () {
    var r = this.root, t = this.track, o = originalsOf(t);
    this.n = o.length;
    var cs = getComputedStyle(t);
    var gap = parseFloat(cs.columnGap) || parseFloat(cs.gap) || 0;
    var avail = t.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    if (!this.n || avail <= 0) return;
    var per = parseFloat(getComputedStyle(r).getPropertyValue('--ssc-per'));
    if (!(per > 0)) {
      var wasOn = r.classList.contains('ssc-tx');
      r.classList.remove('ssc-tx');
      var nat = o[0].getBoundingClientRect().width || avail;
      if (wasOn) r.classList.add('ssc-tx');
      per = Math.max(1, Math.floor((avail + gap + 1) / (nat + gap)));
    }
    if (this.n <= per + 0.01) { // everything fits: static row, no arrows
      if (this.on) this.teardown();
      r.classList.add('ssc-static');
      placeArrows(r, t);
      return;
    }
    r.classList.remove('ssc-static');
    var w = (avail - gap * (Math.ceil(per) - 1)) / per;
    r.style.setProperty('--ssc-w', w + 'px');
    this.per = per;
    this.step = w + gap;
    if (!this.on) this.build();
    r.classList.add('ssc-tx', 'ssc-looping');
    this.i = ((this.i % this.n) + this.n) % this.n;
    this.place(false);
    placeArrows(r, t);
    this.auto();
  };

  Tx.prototype.place = function (animate) {
    var t = this.track;
    var x = -(this.n + this.i) * this.step;
    if (!animate || reduceMotion) t.style.transition = 'none';
    else t.style.transition = '';
    t.style.transform = 'translate3d(' + x + 'px,0,0)';
    if (!animate || reduceMotion) { void t.offsetWidth; t.style.transition = ''; }
  };

  Tx.prototype.normalize = function () {
    if (!this.on) return;
    if (this.i < 0 || this.i >= this.n) {
      this.i = ((this.i % this.n) + this.n) % this.n;
      this.place(false);
    }
  };

  Tx.prototype.go = function (d) {
    if (!this.on) return;
    this.normalize();
    this.i += d;
    this.place(true);
    var self = this;
    clearTimeout(this.nt);
    this.nt = setTimeout(function () { self.normalize(); }, 700); // fallback if transitionend is skipped
  };

  Tx.prototype.auto = function () {
    var self = this;
    var sec = parseFloat(this.root.getAttribute('data-ssc-autoplay')) || 0;
    clearInterval(this.timer);
    if (!sec || !this.on || reduceMotion) return;
    this.timer = setInterval(function () {
      if (self.hover || self.focus || self.down || !self.visible || document.hidden) return;
      self.go(1);
    }, Math.max(2, sec) * 1000);
  };

  Tx.prototype.bind = function () {
    var self = this, t = this.track, clip = this.clip, root = this.root;
    function onArrow(dir) {
      return function (e) {
        e.preventDefault();
        e.stopPropagation(); // keep legacy document-level arrow handlers out
        self.go(dir);
        self.auto(); // restart the autoplay countdown after a manual move
      };
    }
    if (this.prev) this.prev.addEventListener('click', onArrow(-1));
    if (this.next) this.next.addEventListener('click', onArrow(1));
    t.addEventListener('transitionend', function (e) {
      if (e.target === t && e.propertyName === 'transform') { clearTimeout(self.nt); self.normalize(); }
    });
    root.addEventListener('keydown', function (e) {
      if (!self.on) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); self.go(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(-1); }
    });
    // keyboard focus on an off-screen card brings it into view
    root.addEventListener('focusin', function (e) {
      self.focus = true;
      clip.scrollLeft = 0;
      if (!self.on) return;
      var o = originalsOf(t);
      for (var k = 0; k < o.length; k++) {
        if (o[k].contains(e.target)) {
          var vis = Math.floor(self.per);
          if (k < self.i || k >= self.i + vis) { self.i = k; self.place(true); }
          break;
        }
      }
    });
    root.addEventListener('focusout', function () { self.focus = false; });
    root.addEventListener('mouseenter', function () { self.hover = true; });
    root.addEventListener('mouseleave', function () { self.hover = false; });

    // swipe / drag
    // SS 2026-10-08 (client: Olivia): "not swipeable on phone". Touch input now has its own touchstart/touchmove/touchend
    // path (touchmove is non-passive, so a horizontal swipe calls preventDefault and the page does not also scroll);
    // pointer events are used for mouse / pen only, so iOS/Android pointercancel quirks can no longer drop a swipe.
    var sx = 0, sy = 0, dx = 0, drag = false;
    function start(x, y) { self.down = true; drag = false; dx = 0; sx = x; sy = y; }
    function move(x, y) { // returns true while a horizontal drag is in progress
      if (!self.down) return false;
      var mx = x - sx, my = y - sy;
      if (!drag) {
        if (Math.abs(mx) > 8 && Math.abs(mx) > Math.abs(my)) {
          drag = true;
          self.normalize();
          t.style.transition = 'none';
        } else if (Math.abs(my) > 10) { self.down = false; }
        return drag;
      }
      dx = mx;
      t.style.transform = 'translate3d(' + (-(self.n + self.i) * self.step + dx) + 'px,0,0)';
      return true;
    }
    function up() {
      if (!self.down) return;
      self.down = false;
      if (!drag) return;
      drag = false;
      var lim = Math.ceil(self.per);
      var k = Math.round(-dx / self.step);
      if (k === 0 && Math.abs(dx) > self.step * 0.15) k = dx < 0 ? 1 : -1;
      k = Math.max(-lim, Math.min(lim, k));
      self.suppress = true;
      setTimeout(function () { self.suppress = false; }, 60);
      self.i += k;
      self.place(true);
      clearTimeout(self.nt);
      self.nt = setTimeout(function () { self.normalize(); }, 700);
      self.auto();
    }
    clip.addEventListener('dragstart', function (e) { if (self.on) e.preventDefault(); });
    clip.addEventListener('pointerdown', function (e) {
      if (!self.on || e.pointerType === 'touch' || (e.pointerType === 'mouse' && e.button !== 0)) return;
      start(e.clientX, e.clientY);
    });
    window.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      move(e.clientX, e.clientY);
    }, { passive: true });
    window.addEventListener('pointerup', function (e) { if (e.pointerType !== 'touch') up(); }, { passive: true });
    window.addEventListener('pointercancel', function (e) { if (e.pointerType !== 'touch') up(); }, { passive: true });
    clip.addEventListener('touchstart', function (e) {
      if (!self.on || e.touches.length !== 1) { self.down = false; return; }
      start(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    clip.addEventListener('touchmove', function (e) {
      if (!self.down || e.touches.length !== 1) return;
      if (move(e.touches[0].clientX, e.touches[0].clientY) && e.cancelable) e.preventDefault();
    }, { passive: false });
    clip.addEventListener('touchend', up, { passive: true });
    clip.addEventListener('touchcancel', up, { passive: true });
    clip.addEventListener('click', function (e) {
      if (self.suppress) { e.preventDefault(); e.stopPropagation(); self.suppress = false; }
    }, true);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) { self.visible = en[0].isIntersecting; }).observe(root);
    }
    if ('ResizeObserver' in window) {
      var rt, lw = 0;
      new ResizeObserver(function () {
        var w = clip.clientWidth;
        if (w === lw) return;
        lw = w;
        clearTimeout(rt);
        rt = setTimeout(function () { self.refresh(); }, 60);
      }).observe(clip);
    } else {
      window.addEventListener('resize', function () { self.refresh(); });
    }
    window.addEventListener('load', function () { self.refresh(); });
  };

  /* SS 2026-10-08 (client: Olivia): price level across a row with no irregular gap. Every card title in one row
     (same parent + same top: a carousel track or one grid row) gets min-height = the tallest title's natural
     (clamped, max 2 lines) height in that row. All 1-line titles -> no extra space. Re-run after fonts load,
     on load, resize and DOM changes. Replaces the per-section equaliser in ss-related-carousel. */
  function eqAll() {
    var ts = document.querySelectorAll('.ssc .ssc-card .ssc-title');
    var i, g = {}, keys = [], t, c, k, h;
    for (i = 0; i < ts.length; i++) ts[i].style.minHeight = '';
    for (i = 0; i < ts.length; i++) {
      t = ts[i]; c = t.closest('.ssc-card');
      if (!c || !c.parentNode || !t.offsetHeight) continue;
      if (!c.parentNode.__sscEqId) c.parentNode.__sscEqId = 'p' + (++eqSeq);
      k = c.parentNode.__sscEqId + ':' + Math.round(c.offsetTop);
      if (!g[k]) { g[k] = { m: 0, l: [] }; keys.push(k); }
      h = t.getBoundingClientRect().height;
      g[k].l.push(t);
      if (h > g[k].m) g[k].m = h;
    }
    keys.forEach(function (key) {
      var r = g[key];
      r.l.forEach(function (el) { if (el.getBoundingClientRect().height < r.m - 0.5) el.style.minHeight = r.m + 'px'; });
    });
  }
  var eqSeq = 0, eqT = 0;
  function eqSoon() { clearTimeout(eqT); eqT = setTimeout(eqAll, 80); }
  window.addEventListener('resize', eqSoon);
  window.addEventListener('load', eqSoon);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(eqSoon);

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-ssc-loop]');
    for (var i = 0; i < roots.length; i++) {
      var r = roots[i];
      if (r.__sscLoop) continue;
      if (!r.querySelector('[data-ssc-track]')) continue;
      r.__sscLoop = r.tagName === 'SLIDER-COMPONENT' ? new Loop(r) : new Tx(r);
    }
    eqSoon();
  }

  // Touch devices: a tap on the image box should behave like the card link
  // even where the image link is aria-hidden (it already is a real <a>), so
  // nothing to do. Hover test hook: toggle .is-hover for automation.
  window.SSCardCarousel = { init: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  var mo;
  if ('MutationObserver' in window) {
    var pending = false;
    mo = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () { pending = false; initAll(); }, 30); // not rAF: must also run in background tabs
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
